import { ExternalLink } from '@/components/ExternalLink/ExternalLink'
import type { KinesisExtensionConfiguration } from '@/generated/http-clients/v3ApiSchemas'
import { NBSP } from '@/utils'
import { Box, Code, List, VStack } from '@chakra-ui/react'
import {
  ConfigFormManager,
  type FormModel,
  buildFormModel,
  convertJsonToXml,
  convertToXMLDocument,
  fetchXMLDocument,
  serializeDocument,
} from '@hivemq/xsd-forms'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { kinesisTransform } from '../utils/kinesisUtils'
import style from './ConfigureKinesisIntegrationV3.module.css'

type KinesisExtensionPageProps = {
  initialConfig: KinesisExtensionConfiguration | undefined
  onSubmit: (json?: KinesisExtensionConfiguration) => void
}

const KINESIS_UI_SCHEMA = {
  $titleHide: true,
  'hivemq-amazon-kinesis-extension': {
    $titleHide: true,
    'aws-credential-profiles': {
      $titleHide: true,
      'aws-credential-profile': {
        $title: 'Authentication',
        $description:
          'Add credentials to authenticate your HiveMQ Cloud Cluster with AWS Kinesis.',
        'access-key-id': {
          $label: 'Access Key ID',
          $placeholder: 'AWS Access Key ID',
        },
        'secret-access-key': {
          $label: 'Secret Access Key',
          $placeholder: 'AWS Secret Access Key',
        },
      },
    },
    'mqtt-to-kinesis-routes': {
      $titleHide: true,
      'mqtt-to-kinesis-route': {
        $title: 'MQTT to Amazon Kinesis',
        $description: 'Create mapping from MQTT to AWS Kinesis.',
        region: {
          $label: 'Amazon Kinesis region',
          $placeholder: 'us-west-1',
          $description: 'Set the region were your Kinesis instance is located',
        },
        'mqtt-topic-filters': {
          $titleHide: true,
          'mqtt-topic-filter': {
            $label: 'MQTT topic filter',
            $placeholder:
              'MQTT topic filter that you want to forward to Kinesis',
            $description: (
              <>
                Define the MQTT topics you want to stream data from to Amazon
                Kinesis by defining an MQTT topic filter.
                <br />
                You can also{NBSP}
                <ExternalLink
                  href="https://www.hivemq.com/blog/mqtt-essentials-part-5-mqtt-topics-best-practices/"
                  rel="noopener nofollow"
                  target="_blank"
                >
                  follow our guide
                </ExternalLink>
                {NBSP}
                and use wildcards:
                <List.Root pl={1} mt={1}>
                  <List.Item>
                    <Code px={2}>#</Code> Multi level wildcard
                  </List.Item>
                  <List.Item>
                    <Code px={2}>+</Code> Single level wildcard
                  </List.Item>
                </List.Root>
              </>
            ),
          },
        },
        processor: {
          $titleHide: true,
          mapping: {
            $titleHide: true,
            'kinesis-streams': {
              $titleHide: true,
              'kinesis-stream': {
                $titleHide: true,
                name: {
                  $label: 'Amazon Kinesis data stream',
                  $description:
                    'Set the name of your Amazon Kinesis data stream',
                  $placeholder: 'your-kinesis-data-stream-name',
                },
              },
            },
          },
        },
      },
    },
  },
}

const baseXML = `
<hivemq-amazon-kinesis-extension>
    <aws-credential-profiles>
        <aws-credential-profile>
            <id>default</id>
            <access-key-id></access-key-id>
            <secret-access-key></secret-access-key>
        </aws-credential-profile>
    </aws-credential-profiles>
    <mqtt-to-kinesis-routes>
        <mqtt-to-kinesis-route>
            <id>default</id>
            <aws-credential-profile-id>default</aws-credential-profile-id>
            <enabled>true</enabled>
            <region></region>
            <mqtt-topic-filters>
                <mqtt-topic-filter></mqtt-topic-filter>
            </mqtt-topic-filters>
            <processor>
                <mapping>
                    <kinesis-streams>
                        <kinesis-stream>
                            <name></name>
                            <partition-key>
                                <mqtt-topic/>
                            </partition-key>
                            <explicit-hash-key>
                                <random/>
                            </explicit-hash-key>
                        </kinesis-stream>
                    </kinesis-streams>
                </mapping>
            </processor>
        </mqtt-to-kinesis-route>
    </mqtt-to-kinesis-routes>
</hivemq-amazon-kinesis-extension>
`

export function KinesisExtensionPage({
  initialConfig,
  onSubmit,
}: Readonly<KinesisExtensionPageProps>) {
  const { t } = useTranslation('features', { useSuspense: false })

  const [formModel, setFormModel] = useState<FormModel | null>(null)
  const xsd = fetchXMLDocument('/xsd/kinesis/xsd/schema.xsd')

  function assignFormModel(xml: string) {
    buildFormModel('', xsd, convertToXMLDocument(xml), KINESIS_UI_SCHEMA).then(
      (data) => {
        setFormModel(data)
      },
    )
  }

  // reload the form model every time the base XML or the expert mode changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: we do not want to reload when assignFormModel changes
  useEffect(() => {
    // if an initial config is passed, transform it to XML and load it
    if (initialConfig) {
      const jsonInXml = convertJsonToXml(initialConfig)
      assignFormModel(jsonInXml)
      return
    }

    // otherwise load the empty Kafka XML
    assignFormModel(baseXML)
  }, [initialConfig])

  return (
    <Box
      p={4}
      backgroundColor="background.bg-base"
      border="1px solid"
      borderColor="border.border-base"
      borderRadius={6}
    >
      <VStack gap={4} align="stretch">
        {formModel && (
          <div className={style.kinesisConfiguration}>
            <ConfigFormManager
              key={`config_manager_${formModel.name}`}
              xsd={formModel.xsd.rootElement}
              title={formModel.title}
              keyRefs={formModel.xsd.keyRefs}
              uiSchema={formModel.uiSchema}
              xml={serializeDocument(formModel.xml)}
              onSubmit={(_xml, _files, json) => {
                const kinesisConfig: KinesisExtensionConfiguration =
                  kinesisTransform(json)
                onSubmit(kinesisConfig)
              }}
              submitButtonText={t('extensions.saveConfiguration') ?? undefined}
            />
          </div>
        )}
      </VStack>
    </Box>
  )
}
